import { Checkbox, Input, Select } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'

const ADMIN_GROUP = 'SYS.admins'
const EMAIL_OPTION = '__email__'

/*
 * The two fields that decide how an invitation code may be used.
 *
 * "Admin use only" retires a code without deleting it: an admin can still
 * create users with it, and can still pick it when approving someone, but
 * nobody can register with it themselves.
 *
 * "Require admin approval" holds anyone registering with this code until an
 * admin decides, and names who hears about it. That target is stored as the
 * invitation's `approvalNotify` -- an email address, or a group id whose
 * members are told -- and its presence is what turns approval on. Approving
 * stays admin-only wherever the notice lands, so a non-admin named here is
 * told about a registration and still cannot decide it.
 */
export function readApprovalFields(values) {
  const notify = values.approvalRequired
    ? values.approvalNotifyTarget === EMAIL_OPTION
      ? (values.approvalNotifyEmail || '').trim().toLowerCase()
      : values.approvalNotifyTarget || ADMIN_GROUP
    : ''
  return {
    adminOnly: !!values.adminOnly,
    approvalNotify: notify,
  }
}

export function approvalFieldDefaults(record = {}) {
  const notify =
    typeof record.approvalNotify === 'string'
      ? record.approvalNotify.trim()
      : ''
  const isEmail = notify.includes('@')
  return {
    approvalRequired: !!notify,
    approvalNotifyTarget: isEmail ? EMAIL_OPTION : notify || ADMIN_GROUP,
    approvalNotifyEmail: isEmail ? notify : '',
  }
}

const InvitationAccessFields = ({
  FormItem,
  getFieldDecorator,
  getFieldValue,
  groups,
  defaults,
}) => {
  const approvalRequired = getFieldValue('approvalRequired')
  const target = getFieldValue('approvalNotifyTarget')
  const groupOptions = (groups || [])
    .filter((group) => group.id !== ADMIN_GROUP)
    .map((group) => ({ value: group.id, label: group.name }))

  return (
    <>
      <FormItem
        label="Admin use only"
        extra="Nobody can register with this code. Admins can still create users with it, and still choose it when approving a registration. Use this to retire a code without losing the access it grants."
      >
        {getFieldDecorator('adminOnly', {
          valuePropName: 'checked',
          initialValue: !!defaults.adminOnly,
        })(<Checkbox />)}
      </FormItem>
      <FormItem
        label="Require admin approval"
        extra="Anyone registering with this code waits until an admin approves them. They cannot sign in before that."
      >
        {getFieldDecorator('approvalRequired', {
          valuePropName: 'checked',
          initialValue: defaults.approvalRequired,
        })(<Checkbox />)}
      </FormItem>
      {approvalRequired && (
        <FormItem
          label="Send approval requests to"
          extra="Only admins can approve, wherever the notice goes."
        >
          {getFieldDecorator('approvalNotifyTarget', {
            initialValue: defaults.approvalNotifyTarget,
          })(
            <Select
              style={{ width: '100%' }}
              options={[
                { value: ADMIN_GROUP, label: 'All admins' },
                ...groupOptions,
                { value: EMAIL_OPTION, label: 'A specific email address…' },
              ]}
            />,
          )}
        </FormItem>
      )}
      {approvalRequired && target === EMAIL_OPTION && (
        <FormItem label="Email address">
          {getFieldDecorator('approvalNotifyEmail', {
            initialValue: defaults.approvalNotifyEmail,
            rules: [
              {
                required: true,
                type: 'email',
                message: 'A valid email address is required',
              },
            ],
          })(<Input placeholder="name@example.com" />)}
        </FormItem>
      )}
    </>
  )
}

InvitationAccessFields.propTypes = {
  FormItem: PropTypes.elementType.isRequired,
  getFieldDecorator: PropTypes.func.isRequired,
  getFieldValue: PropTypes.func.isRequired,
  groups: PropTypes.array,
  defaults: PropTypes.object,
}

InvitationAccessFields.defaultProps = { groups: [], defaults: {} }

export default InvitationAccessFields
